/**
 * Client-side pagination for the review preview.
 *
 * The PDF is paginated by the render worker's measure-then-split pass (apps/render-worker/src/measure.ts
 * + the shared `paginate` arithmetic). The worker runs that measurement inside Chromium via
 * `page.evaluate`; the preview is already in a browser, so the same DOM measurement runs directly here on
 * the rendered pages, feeds the SAME `paginate()` (imported from @pdmg/reporting-bindings), and rebuilds
 * each flowing page into real page-sized sheets — so the preview cascades across pages exactly like the
 * PDF instead of clipping one over-long page.
 *
 * It operates on static markup (the preview renders the report with `renderToStaticMarkup`), so there is
 * no React tree to reconcile against the node moves. Measurements are taken at the preview's current zoom;
 * because zoom scales every length uniformly the row-per-page result is unchanged.
 */

import { paginate, type MeasuredBlock, type PaginateResult } from '@pdmg/reporting-bindings';

// The attributes the SSR pass stamps onto blocks — must match apps/render-worker/src/measure.ts.
const ATTRS = {
  block: 'data-block-id',
  flow: 'data-flow',
  repeatHeader: 'data-repeat-header',
  rows: 'data-rows',
  header: 'data-row-header',
  caption: 'data-row-caption',
} as const;

/** Must match FOOTER_SAFETY_IN in apps/render-worker/src/render.tsx so the preview breaks where the PDF does. */
const FOOTER_SAFETY_IN = 0.6;

function measureGeometry(pageEl: HTMLElement): { contentHeight: number; pageHeight: number } {
  const body = pageEl.querySelector('.page-body') as HTMLElement | null;
  const pageHeight = pageEl.getBoundingClientRect().height;
  if (!body) return { contentHeight: pageHeight, pageHeight };
  const style = getComputedStyle(body);
  const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
  const chrome = pageHeight - body.getBoundingClientRect().height;
  /*
   * The copyright/page-number line is in-flow here but lives in Chromium's margin box in the PDF, so the
   * PDF measures without it. Give its height back, or the preview has ~12px less room than the PDF and
   * breaks earlier (Campaign Details split 3+2 where the PDF fits 5).
   */
  const notice = pageEl.querySelector('.page-foot .page-notice') as HTMLElement | null;
  const noticeHeight = notice ? notice.getBoundingClientRect().height : 0;
  return { contentHeight: pageHeight - chrome - padding + noticeHeight, pageHeight };
}

function measureBlocks(pageEl: HTMLElement): MeasuredBlock[] {
  const out: MeasuredBlock[] = [];
  const blocks = (Array.from(pageEl.querySelectorAll(`[${ATTRS.block}]`)) as HTMLElement[])
    // Only top-level blocks: a nested block inside a SectionCard is measured as part of its parent.
    .filter((el) => {
      const parent = el.parentElement?.closest(`[${ATTRS.block}]`);
      return !parent || !pageEl.contains(parent);
    });
  // Blocks are laid out with flex `gap`, not margins — read the container's row-gap so spacing is counted.
  const gap = parseFloat(getComputedStyle(pageEl).rowGap) || 0;

  for (const el of blocks) {
    const style = getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    const tbody = el.querySelector(`[${ATTRS.rows}]`) as HTMLElement | null;
    const flow: 'atomic' | 'breakable' =
      tbody ? 'breakable' : (el.getAttribute(ATTRS.flow) === 'breakable' ? 'breakable' : 'atomic');

    const measured: MeasuredBlock = {
      id: el.getAttribute(ATTRS.block) ?? '',
      flow,
      height: rect.height,
      marginBottom: (parseFloat(style.marginBottom) || 0) + gap,
    };

    if (flow === 'breakable' && tbody) {
      const rows = Array.from(tbody.children) as HTMLElement[];
      const header = el.querySelector(`[${ATTRS.header}]`) as HTMLElement | null;
      const caption = el.querySelector(`[${ATTRS.caption}]`) as HTMLElement | null;
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const captionHeight = caption ? caption.getBoundingClientRect().height : 0;
      // Flex-gapped rows (the creative cards) carry spacing no row rect includes — count it per row,
      // mirroring the PDF measurer, so the preview splits where the PDF does. Tables: row-gap 0.
      const rowGap = parseFloat(getComputedStyle(tbody as Element).rowGap) || 0;
      const rowHeights = rows.map((r) => r.getBoundingClientRect().height + rowGap);
      const rowsTotal = rowHeights.reduce((a, b) => a + b, 0);
      measured.rows = {
        headerHeight,
        repeatHeader:
          (header?.getAttribute(ATTRS.repeatHeader) ?? el.getAttribute(ATTRS.repeatHeader)) === 'true',
        rowHeights,
        footerHeight: captionHeight,
        chromeHeight: Math.max(0, rect.height - headerHeight - rowsTotal - captionHeight),
      };
    }
    out.push(measured);
  }
  return out;
}

/** Rebuild one flowing page element into N page elements per the plan (moving nodes, like the worker). */
function applyPlan(source: HTMLElement, plan: PaginateResult): HTMLElement[] {
  const body = source.querySelector('.page-body') as HTMLElement | null;
  if (!body) return [source];

  const blocks = new Map<string, HTMLElement>();
  for (const el of Array.from(body.querySelectorAll(`[${ATTRS.block}]`)) as HTMLElement[]) {
    const id = el.getAttribute(ATTRS.block)!;
    if (!blocks.has(id)) blocks.set(id, el);
  }

  const made: HTMLElement[] = [];
  for (const planned of plan.pages) {
    const sheet = source.cloneNode(false) as HTMLElement;
    // Chrome (header band, footer) is re-cloned per sheet; only the body differs.
    Array.from(source.children).forEach((child) => {
      if (!child.classList.contains('page-body')) sheet.appendChild(child.cloneNode(true));
    });
    const sheetBody = body.cloneNode(false) as HTMLElement;
    const footer = sheet.querySelector('.page-foot');
    if (footer) sheet.insertBefore(sheetBody, footer);
    else sheet.appendChild(sheetBody);

    for (const fragment of planned.fragments) {
      const original = blocks.get(fragment.blockId);
      if (!original) continue;

      if (fragment.kind === 'whole') {
        sheetBody.appendChild(original.cloneNode(true));
        continue;
      }

      const shell = original.cloneNode(true) as HTMLElement;
      const tbody = shell.querySelector(`[${ATTRS.rows}]`) as HTMLElement | null;
      if (!tbody) { sheetBody.appendChild(shell); continue; }

      const allRows = Array.from(tbody.children);
      tbody.innerHTML = '';
      for (let i = fragment.from; i < fragment.to; i++) {
        if (allRows[i]) tbody.appendChild(allRows[i]);
      }
      if (!fragment.withHeader) shell.querySelectorAll(`[${ATTRS.header}]`).forEach((h) => h.remove());
      if (!fragment.withFooter) shell.querySelectorAll(`[${ATTRS.caption}]`).forEach((c) => c.remove());
      if (!fragment.isFirst) {
        shell.querySelectorAll('.card-title').forEach((t) => t.remove());
        shell.setAttribute('data-continued', 'true');
        const cardBody = tbody.closest('.card-body');
        if (cardBody) {
          let keep: Element = tbody;
          while (keep.parentElement && keep.parentElement !== cardBody) keep = keep.parentElement;
          Array.from(cardBody.children).forEach((child) => { if (child !== keep) child.remove(); });
        }
      }
      sheetBody.appendChild(shell);
    }
    made.push(sheet);
  }

  made.forEach((sheet) => source.parentNode!.insertBefore(sheet, source));
  source.remove();
  return made;
}

/**
 * Split every flowing page under `container` into real page-sized sheets. `heightIn` is the page height
 * (from the template theme), used to convert the footer-safety reserve from inches to the current pixels.
 * Fixed / summary pages are left exactly as they are.
 */
export function paginatePreview(container: ParentNode, heightIn: number): void {
  const flowing = Array.from(container.querySelectorAll('.page[data-mode="flowing"]')) as HTMLElement[];
  for (const pageEl of flowing) {
    const geometry = measureGeometry(pageEl);
    if (geometry.pageHeight <= 0) continue;
    const blocks = measureBlocks(pageEl);
    if (blocks.length === 0) continue;
    const pxPerIn = geometry.pageHeight / heightIn;
    const usableHeight = Math.max(1, geometry.contentHeight - FOOTER_SAFETY_IN * pxPerIn);
    const plan = paginate(blocks, { pageHeight: usableHeight });
    if (plan.pages.length <= 1) continue; // fits one sheet — nothing to split
    applyPlan(pageEl, plan);
  }
  /*
   * The on-screen "Page N of M" is stamped per TEMPLATE page before splitting, so every sheet cut from a
   * flowing page repeated the same number ("Page 3 of 3" on pages 3, 4 and 5). Renumber the final sheets,
   * as Chromium's margin box does in the PDF.
   */
  const sheets = Array.from(container.querySelectorAll('.page')) as HTMLElement[];
  sheets.forEach((sheet, i) => {
    const num = sheet.querySelector('.page-notice .page-num');
    if (num) num.textContent = `Page ${i + 1} of ${sheets.length}`;
  });
}
